import { useMemo } from "react";

import { getSharedTableData } from "@/api/form";
import { useQuery } from "@tanstack/react-query";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { MoonLoader } from "react-spinners";

const modeLabels: Record<string, string> = {
  permanent: "لینک دائمی",
  expiring: "لینک زمان دار",
  "multi-use": "لینک چندبار مصرف محدود",
  internal: "لینک داخلی"
};

const SharedTableViewPage = () => {
  const { shareCode } = useParams<{ shareCode: string }>();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") ?? undefined;

  const { data, isLoading, isError } = useQuery({
    queryKey: ["shared-table", shareCode, token],
    enabled: !!shareCode,
    retry: false,
    queryFn: async () => {
      return await getSharedTableData(shareCode!, token);
    }
  });

  const modeLabel = useMemo(() => {
    if (!data?.mode) return "نامشخص";
    return modeLabels[data.mode] ?? data.mode;
  }, [data?.mode]);

  if (isLoading) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-slate-50">
        <MoonLoader color="#0099A5" size={46} />
      </div>
    );
  }

  if (isError || !data) {
    const loginRedirect = encodeURIComponent(
      `${window.location.pathname}${window.location.search}`
    );

    return (
      <div className="flex min-h-screen w-full items-center justify-center bg-slate-100 px-4">
        <div className="w-full max-w-xl rounded-2xl border border-slate-200 bg-white p-8 text-right shadow-sm">
          <h1 className="text-xl font-bold text-slate-800">
            عدم دسترسی به لینک اشتراک گذاری
          </h1>
          <p className="mt-3 text-sm leading-7 text-slate-600">
            لینک معتبر نیست یا مدت اعتبار آن پایان یافته است. اگر لینک داخلی
            دریافت کرده اید، ابتدا وارد سامانه شوید.
          </p>
          <div className="mt-6 flex flex-wrap justify-end gap-3">
            <Link
              to={`/dashboard/login?returnUrl=${loginRedirect}`}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-white"
            >
              ورود به سامانه
            </Link>
            <Link
              to="/dashboard"
              className="rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700"
            >
              بازگشت به داشبورد
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-8">
      <div className="mx-auto w-full max-w-6xl space-y-4 px-4">
        <div className="rounded-xl border border-slate-200 bg-white p-5 text-right shadow-sm">
          <p className="text-xs text-slate-500">
            کد اشتراک گذاری: {data.shareCode}
          </p>
          <h1 className="mt-2 text-xl font-bold text-slate-800">
            نمایش جدول اشتراک گذاری شده
          </h1>
          <div className="mt-4 flex flex-wrap justify-end gap-2 text-xs">
            <span className="rounded border border-slate-200 bg-slate-50 px-2 py-1 text-slate-700">
              نوع لینک: {modeLabel}
            </span>
            {data.expiresAtUtc ? (
              <span className="rounded border border-amber-200 bg-amber-50 px-2 py-1 text-amber-700">
                اعتبار تا: {new Date(data.expiresAtUtc).toLocaleString("fa-IR")}
              </span>
            ) : null}
            {typeof data.maxUseCount === "number" ? (
              <span className="rounded border border-slate-200 bg-slate-50 px-2 py-1 text-slate-700">
                تعداد مجاز: {data.maxUseCount} | استفاده شده: {data.usedCount}
              </span>
            ) : null}
          </div>
        </div>

        {data.tables.map((table, index) => (
          <div
            key={`${table.tableId ?? "table"}-${index}`}
            className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm"
          >
            <div className="border-b border-slate-200 bg-slate-100 px-5 py-3 text-right text-sm font-semibold text-slate-700">
              {table.title || `جدول ${index + 1}`}
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full border-collapse text-right text-sm">
                <thead>
                  <tr className="bg-slate-50">
                    {table.headers.map((header, headerIndex) => (
                      <th
                        key={`${header}-${headerIndex}`}
                        className="border-b border-slate-200 px-4 py-3 font-semibold text-slate-700"
                      >
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {table.rows.length > 0 ? (
                    table.rows.map((row, rowIndex) => (
                      <tr
                        key={rowIndex}
                        className="odd:bg-white even:bg-slate-50/60"
                      >
                        {row.map((cell, cellIndex) => (
                          <td
                            key={`${rowIndex}-${cellIndex}`}
                            className="border-b border-slate-100 px-4 py-3 text-slate-700"
                          >
                            {cell}
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={Math.max(1, table.headers.length)}
                        className="px-4 py-6 text-center text-slate-500"
                      >
                        داده ای برای نمایش ثبت نشده است
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SharedTableViewPage;
